import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Search, Users, Clock3 } from 'lucide-react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { listCompanyInvites, createCompanyInvite } from '../../services/companyService';
import '../../styles/StaffVipVisitorsPage.css'; // Reuse styles for the layout

export default function CompanyInvitesPage() {
  const [invites, setInvites] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCompanyInvites({ search });
      if (res.success) {
        setInvites(res.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors du chargement des invités');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => fetchInvites(), 250);
    return () => clearTimeout(timeoutId);
  }, [fetchInvites]);

  const onSubmit = async (data) => {
    setError('');
    setSuccess('');
    try {
      const payload = { ...data };
      const res = await createCompanyInvite(payload);
      if (res.success) {
        setSuccess('Invité enregistré avec succès. Un email avec son code QR lui a été envoyé.');
        reset();
        fetchInvites();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors de l\'enregistrement de l\'invité');
    }
  };

  return (
    <div className="vip-visitors-page">
      <div className="staff-vip-layout">
        
        {/* Form to add an invitee */}
        <form onSubmit={handleSubmit(onSubmit)} className="staff-vip-form-card">
          <div className="staff-vip-form-header">
            <h2>Inviter quelqu'un</h2>
            <p>Remplissez ce formulaire pour générer un pass QR qui lui sera envoyé par email.</p>
          </div>

          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner">{success}</div>}

          <div className="staff-vip-form-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Nom complet *</label>
              <input {...register('fullName', { required: 'Le nom est requis' })} placeholder="Nom du visiteur" />
              {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Email (pour l'envoi du QR) *</label>
              <input type="email" {...register('email', { required: 'L\'email est requis pour envoyer le pass' })} placeholder="invite@email.com" />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Téléphone (optionnel)</label>
              <input type="tel" {...register('phone')} placeholder="+216 XX XXX XXX" />
            </div>

            <div className="form-group">
              <label>Date prévue *</label>
              <input type="date" {...register('expectedDate', { required: 'La date est requise' })} />
              {errors.expectedDate && <span className="field-error">{errors.expectedDate.message}</span>}
            </div>

            <div className="form-group">
              <label>Heure prévue (optionnelle)</label>
              <input type="time" {...register('expectedTime')} />
            </div>
          </div>

          <div className="form-group">
            <label>Remarques (optionnelles)</label>
            <textarea {...register('comments')} rows={3} placeholder="Détails supplémentaires..." />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer l\'invitation'}
            </button>
          </div>
        </form>

        {/* List of invitees */}
        <section className="staff-vip-list-card">
          <div className="staff-vip-list-header">
            <div>
              <h2>Mes invités</h2>
              <p>Personnes que vous avez invitées récemment.</p>
            </div>
            <label className="staff-vip-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>

          {loading ? (
            <LoadingSkeleton lines={7} />
          ) : invites.length === 0 ? (
            <div className="staff-vip-empty">Vous n'avez invité personne pour le moment.</div>
          ) : (
            <div className="staff-vip-list">
              {invites.map((item) => (
                <article key={item.visit_id} className="staff-vip-item">
                  <div className="staff-vip-item-top">
                    <span className="staff-vip-pill" style={{ background: 'var(--primary)', color: 'white' }}>
                      <Users size={14} fill="currentColor" /> Invité
                    </span>
                    <span className={`staff-vip-status ${item.entry_at ? 'status-arrivé' : 'status-à-venir'}`}>
                      {item.entry_at ? (
                        <>
                          Arrivé <span style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                            ({new Date(item.entry_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})
                          </span>
                        </>
                      ) : 'À venir'}
                    </span>
                  </div>
                  <h3>{item.full_name}</h3>
                  <p>{item.email}</p>
                  <div className="staff-vip-time">
                    <Clock3 size={15} />
                    <span>{item.expected_at ? new Date(item.expected_at).toLocaleString('fr-FR') : 'Date non fixée'}</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
